import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ISCDUser } from '@app-root-interfaces';
import { RootServicesModule } from './root-services.module';
import { environment } from '@app-environment';
import { HttpClient } from '@angular/common/http';
import { map, switchMap, tap } from 'rxjs/operators';
import { ScdLocation } from '../interfaces/information-scd-location';
import { ScdOrganization } from '../interfaces/information-scd-organization.interface';
import { SalLocation } from '../interfaces/information-sal-location';
import { AuthService } from './auth.service';

interface DnEncoded {
    c: string;
    o: string;
    ou?: string;
    l?: string;
}

@Injectable({
    providedIn: RootServicesModule
})
export class ScdService {
    private http = inject(HttpClient);
    private authService = inject(AuthService);

    public searchUser(searchTerm: string): Observable<ISCDUser[]> {
        const url = new URL(`${environment.gatewayAPI}/scd/persons`);
        url.searchParams.append('search', searchTerm);
        return this.http.get<ISCDUser[]>(url.toString());
    }

    public getScdUser(gid: string): Observable<ISCDUser> {
        const gidParameter = gid.toUpperCase();
        return this.http.get<ISCDUser[]>(`${environment.gatewayAPI}/scd/persons?search=${gidParameter}`).pipe(
            tap((users) => console.log(users)),
            map((users) => users.find((u) => !u.sponsor) ?? users[0]), // remove entries who have a sponsor to always get the main scd entry.
            map((user) => ({
                ...user,
                initials: user?.initials || `${user.firstName.substr(0, 1)}${user.surName.substr(0, 1)}`
            })),
            map((user) => {
                const newUser: ISCDUser = { ...user };
                const keys = Object.keys(user);
                keys.map((key) => {
                    newUser[key] = user[key] || '';
                });
                return newUser;
            })
        );
    }

    getScdLocation(profile: ISCDUser): Observable<ScdLocation> {
        return this.queryScdLocation(profile.location).pipe(
            map((data) => data.find((loc) => this.isProfileInfo(profile, loc)) || ({} as ScdLocation))
        );
    }

    getScdOrganization(profile: ISCDUser): Observable<ScdOrganization> {
        return this.queryScdOrganization(profile.organization).pipe(
            map((data) => data.find((org) => this.isProfileInfo(profile, org)) || ({} as ScdOrganization))
        );
    }

    getSalLocation(profile: ISCDUser): Observable<SalLocation> {
        return this.getScdLocation(profile).pipe(
            switchMap((location) => this.querySalLocation(location.salid)),
            map((locations) => locations[0])
        );
    }

    private isProfileInfo(profile: ISCDUser, info: ScdLocation | ScdOrganization): boolean {
        const dn = this.decodeDN(info.dn);
        return (
            profile.organization.toUpperCase() === dn.o.toUpperCase() &&
            profile.country.toUpperCase() === dn.c.toUpperCase() &&
            (!dn.ou || dn.ou === '-' || profile.department.toUpperCase().startsWith(dn.ou.toUpperCase()))
        );
    }

    private decodeDN(dn: string): DnEncoded {
        return dn.split(',').reduce((acc, info) => {
            const [attr, value] = info.split('=');
            return {
                ...acc,
                [attr]: value
            };
        }, {} as DnEncoded);
    }

    private queryScdLocation(location: string): Observable<ScdLocation[]> {
        const url = new URL(`${environment.gatewayAPI}/scd/locations`);
        url.searchParams.append('location', location.toUpperCase());
        return this.http.get<ScdLocation[]>(url.toString());
    }

    private queryScdOrganization(organization: string): Observable<ScdOrganization[]> {
        const url = new URL(`${environment.gatewayAPI}/scd/organizations`);
        url.searchParams.append('organization', organization.toUpperCase());
        return this.http.get<ScdOrganization[]>(url.toString());
    }

    private querySalLocation(salid: string): Observable<Array<SalLocation>> {
        return this.http.get<Array<SalLocation>>(`${environment.sal.url}/${salid}`, {
            headers: {
                'x-api-key': environment.sal.apiKey
            }
        });
    }
}
